import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { 
	DropdownMenu, 
	DropdownMenuContent, 
	DropdownMenuCheckboxItem, 
	DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu'
import { ListRecordLayout } from '@/components/ListRecordLayout/ListRecordLayout'
import { SectionHeading } from '@/components/ui/section'
import { RecordPanel } from '@/components/ListRecordLayout/RecordPanel'
import { OrdersTable } from '@/components/Orders/OrdersTable'
import { OrdersKanbanShadcn } from '@/components/Orders/OrdersKanbanShadcn'
import { useOrdersQuery } from '@/queries/orders/useOrdersQuery'
import { useUpdateOrderStatus } from '@/queries/orders/useUpdateOrderStatus'
import { type OrderWithContact, getConversationsForOrder } from '@/data/mockOrdersData'
import { useChatBubble } from '@/components/Inbox/ChatBubbleProvider'
import { formatSmartCurrency } from '@/utils/formatCurrency'
import { 
	Search, 
	LayoutGrid, 
	Table2, 
	Loader2,
	Filter,
	Plus,
	ArrowUpDown
} from 'lucide-react'

const ORDER_STATUSES = [
	{ id: 'new_lead', label: 'New Lead' },
	{ id: 'qualified', label: 'Qualified' },
	{ id: 'scheduled', label: 'Scheduled' },
	{ id: 'completed', label: 'Completed' },
	{ id: 'lost', label: 'Lost' },
] as const

const SORT_OPTIONS = [
	{ id: 'manual', label: 'Manual' },
	{ id: 'stage', label: 'Stage' },
	{ id: 'last_activity_newest', label: 'Last activity (newest)' },
	{ id: 'last_activity_oldest', label: 'Last activity (oldest)' },
	{ id: 'amount_highest', label: 'Amount (highest)' }
] as const

export function OrdersPage() {
	const [view, setView] = useState<'table' | 'cards'>('cards')
	const [searchQuery, setSearchQuery] = useState('')
	const [selectedId, setSelectedId] = useState<string | null>(null)
	const [isPanelOpen, setPanelOpen] = useState(false)
	const [statusFilters, setStatusFilters] = useState<string[]>(['new_lead', 'qualified', 'scheduled', 'completed'])
	const [sortMode, setSortMode] = useState<string>('manual')

	const { data: ordersData, isLoading } = useOrdersQuery({
		filters: {
			...(searchQuery ? { search: searchQuery } : {}),
			stage: statusFilters
		}
	})

	const updateOrderStatusMutation = useUpdateOrderStatus()
	const { openFloatingWithOrder, setActiveConversation, state: chatState, setOrderPanelOpen } = useChatBubble()

	// Function to close both panels and reset chat bubble
	const handleCloseAll = () => {
		setPanelOpen(false)
		setOrderPanelOpen(false) // Update global state
		// Dispatch event to close the inbox modal
		window.dispatchEvent(new CustomEvent('closeInboxModal'))
	}

	// Listen for closeOrderPanel events from the inbox modal
	useEffect(() => {
		const handleCloseOrderPanel = () => {
			setPanelOpen(false)
			setOrderPanelOpen(false)
		}

		window.addEventListener('closeOrderPanel', handleCloseOrderPanel)
		return () => window.removeEventListener('closeOrderPanel', handleCloseOrderPanel)
	}, [])

	const handleSelectOrder = (order: OrderWithContact) => {
		setSelectedId(order.id)
		setPanelOpen(true)
		setOrderPanelOpen(true) // Update global state

		// Open inbox with conversation for this order
		// Position chat bubble to the left of order details panel
		// Panel width: 599px (actual width), Inbox width: 480px, Gap: 12px, Bubble width: 64px
		const bubblePosition = { bottom: 12, right: 612 } // Adjusted position based on dev tools
		
		openFloatingWithOrder(order.id, bubblePosition)

		// Get the conversation for this order and set it as active
		const conversations = getConversationsForOrder(order.id)
		if (conversations.length > 0) {
			// Use the most recent conversation
			const mostRecentConversation = conversations[0]
			setActiveConversation(mostRecentConversation.id)
		}
	}

	const handleOrderStatusChange = async (orderId: string, newStatus: string) => {
		await updateOrderStatusMutation.mutateAsync({ orderId, newStatus })
	}

	const handleStatusFilterToggle = (statusId: string) => {
		setStatusFilters(prev => 
			prev.includes(statusId)
				? prev.filter(id => id !== statusId)
				: [...prev, statusId]
		)
	}

	const handleSortModeChange = (sortId: string) => {
		setSortMode(sortId)
	}

	const orders = ordersData?.orders || []

	// Calculate totals for the current view
	const totalCount = orders.length
	const totalAmount = orders.reduce((sum, order) => sum + (order.value_cents || 0), 0)

	const listPanel = (
		<div className="flex flex-col h-full">
			<div className="flex items-center justify-between mb-4">
				<div className="flex items-center gap-4">
					<div className="flex items-center gap-2">
						<SectionHeading className="mb-0">Orders</SectionHeading>
						<span className="text-lg text-muted-foreground font-normal leading-7">
							{totalCount} • {formatSmartCurrency(totalAmount)}
						</span>
					</div>
					{/* Search Input */}
					<div className="relative">
						<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
						<Input
							placeholder="Search by name, phone, or email"
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							className="pl-9 w-64 h-9"
						/>
					</div>
				</div>
				
				<div className="flex items-center gap-2">
					{/* Filter Button */}
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="outline" size="sm" className="h-9">
								<Filter className="h-4 w-4" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end" className="w-48">
							<div className="px-2 py-1.5 text-sm font-semibold">Status</div>
							{ORDER_STATUSES.map((status) => (
								<DropdownMenuCheckboxItem
									key={status.id}
									checked={statusFilters.includes(status.id)}
									onCheckedChange={() => handleStatusFilterToggle(status.id)}
									className="cursor-pointer"
								>
									{status.label}
								</DropdownMenuCheckboxItem>
							))}
						</DropdownMenuContent>
					</DropdownMenu>

					{/* Sort Button */}
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="outline" size="sm" className="h-9">
								<ArrowUpDown className="h-4 w-4" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end" className="w-56">
							<div className="px-2 py-1.5 text-sm font-semibold">Sort by</div>
							{SORT_OPTIONS.map((option) => (
								<DropdownMenuCheckboxItem
									key={option.id}
									checked={sortMode === option.id}
									onCheckedChange={() => handleSortModeChange(option.id)}
									className="cursor-pointer"
								>
									{option.label}
								</DropdownMenuCheckboxItem>
							))}
						</DropdownMenuContent>
					</DropdownMenu>

					{/* View Toggle */}
					<div className="flex border rounded-md">
						<Button 
							variant={view === 'table' ? 'default' : 'ghost'} 
							onClick={() => setView('table')} 
							className="rounded-r-none border-r"
						>
							<Table2 className="h-4 w-4" />
						</Button>
						<Button 
							variant={view === 'cards' ? 'default' : 'ghost'} 
							onClick={() => setView('cards')} 
							className="rounded-l-none"
						>
							<LayoutGrid className="h-4 w-4" />
						</Button>
					</div>

					{/* Add Order Button */}
					<Button 
						onClick={() => {
							// TODO: Implement add order functionality
						}}
						className="h-9"
					>
						<Plus className="h-4 w-4 mr-2" />
						Add Order
					</Button>
				</div>
			</div>

			<div className="flex-1 overflow-auto">
				{isLoading ? (
					<div className="flex items-center justify-center h-32">
						<Loader2 className="h-6 w-6 animate-spin" />
						<span className="ml-2">Loading orders...</span>
					</div>
				) : view === 'table' ? (
					<OrdersTable
						orders={orders}
						onSelectOrder={handleSelectOrder}
						selectedOrderId={selectedId || undefined}
						isLoading={isLoading}
					/>
				) : (
					<OrdersKanbanShadcn
						orders={orders}
						onSelectOrder={handleSelectOrder}
						selectedOrderId={selectedId || undefined}
						isLoading={isLoading}
						onOrderStatusChange={handleOrderStatusChange}
						visibleStages={statusFilters}
						sortMode={sortMode}
					/>
				)}
			</div>
		</div>
	)

	const selectedOrder = orders.find(o => o.id === selectedId)
	const recordPanel = (
		<RecordPanel 
			open={isPanelOpen} 
			onClose={() => setPanelOpen(false)} 
			title={selectedOrder?.contact.display_name || 'Order Details'}
			isInboxModalOpen={chatState.isVisible && chatState.mode === 'floating'}
			onCloseAll={handleCloseAll}
		>
			<div className="space-y-4">
				<div className="space-y-2 text-sm">
					<div>
						<span className="font-medium text-foreground">Stage:</span> 
						<span className="ml-2 capitalize">{selectedOrder?.stage.replace('-', ' ')}</span>
					</div>
					<div>
						<span className="font-medium text-foreground">Email:</span> 
						<span className="ml-2">{selectedOrder?.contact.email || 'None'}</span>
					</div>
					<div>
						<span className="font-medium text-foreground">Phone:</span> 
						<span className="ml-2">{selectedOrder?.contact.phone_raw || 'None'}</span>
					</div>
					{selectedOrder?.attribution && (
						<div>
							<span className="font-medium text-foreground">Attribution:</span> 
							<span className="ml-2">
								{selectedOrder.attribution.gclid && 'Google Ads'}
								{selectedOrder.attribution.fbclid && 'Facebook Ads'}
								{!selectedOrder.attribution.gclid && !selectedOrder.attribution.fbclid && 'Direct'}
							</span>
						</div>
					)}
					<div>
						<span className="font-medium text-foreground">Created:</span> 
						<span className="ml-2">
							{selectedOrder ? new Date(selectedOrder.created_at).toLocaleString() : '-'}
						</span>
					</div>
				</div>
				
				{selectedOrder?.value_cents && selectedOrder.value_cents > 0 && (
					<div className="text-center">
						<div className="font-medium text-2xl">
							{new Intl.NumberFormat('en-US', {
								style: 'currency',
								currency: 'USD',
							}).format(selectedOrder.value_cents / 100)}
						</div>
						<div className="text-muted-foreground">Order Value</div>
					</div>
				)}

				{/* TODO: Add action buttons for stage transitions */}
				<div className="pt-4 border-t">
					<div className="text-sm text-muted-foreground">
						Order actions coming soon...
					</div>
				</div>
			</div>
		</RecordPanel>
	)

	return (
		<div className="flex flex-1 min-h-0 px-6 py-6">
			<ListRecordLayout listPanel={listPanel} recordPanel={recordPanel} />
		</div>
	)
}

